import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  PanTool as PanToolIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { ImageViewerProps, ViewerError } from '../../../types/viewer.types';
import { useDirectContent } from '../../../hooks/useDirectContent';
import { fromArrayBuffer } from 'geotiff';

/**
 * Image Viewer component with zoom and pan functionality
 */
const ImageViewer: React.FC<ImageViewerProps> = ({
  contentId,
  metadata,
  zoom = 1,
  onZoomChange,
  onError,
  onLoad,
  allowPan = true,
  allowZoom = true,
  maxZoom = 5,
  minZoom = 0.1,
  className,
  style,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [naturalDimensions, setNaturalDimensions] = useState<{ width: number; height: number } | null>(null);
  const [isTiff, setIsTiff] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const { contentUrl, isDirectDownload } = useDirectContent(contentId, undefined, metadata);

  // Helper function to check if the image is a TIFF
  const isTiffImage = useCallback((mimeType?: string, fileName?: string): boolean => {
    if (mimeType && (mimeType === 'image/tiff' || mimeType === 'image/tif')) {
      return true;
    }
    if (fileName) {
      const ext = fileName.toLowerCase().split('.').pop();
      return ext === 'tif' || ext === 'tiff';
    }
    return false;
  }, []);

  // Helper function to decode TIFF image
  const decodeTiffImage = useCallback(async (url: string): Promise<string> => {
    try {
      console.log('Decoding TIFF image from URL:', url);

      // Fetch the TIFF file as ArrayBuffer
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();

      // Decode TIFF using geotiff
      const tiff = await fromArrayBuffer(arrayBuffer);
      const image = await tiff.getImage();
      const width = image.getWidth();
      const height = image.getHeight();

      console.log('TIFF decoded:', {
        width,
        height,
        fileDirectory: image.fileDirectory
      });

      // Read the raster data - geotiff returns an object with arrays for each channel
      const rasterData = await image.readRasters();

      console.log('Rasters info:', {
        rasterType: typeof rasterData,
        rasterKeys: Object.keys(rasterData),
        width: rasterData.width,
        height: rasterData.height
      });

      // Validate raster data
      if (!rasterData) {
        throw new Error('No raster data returned from TIFF');
      }

      // Extract the actual pixel data arrays
      // geotiff returns data in rasterData[0], rasterData[1], etc. for each channel
      const channels: (Uint8Array | Uint16Array | Float32Array)[] = [];
      let channelIndex = 0;
      while (rasterData[channelIndex] !== undefined) {
        channels.push(rasterData[channelIndex] as Uint8Array | Uint16Array | Float32Array);
        channelIndex++;
      }

      console.log('Channels extracted:', {
        numChannels: channels.length,
        channel0Type: channels[0]?.constructor.name,
        channel0Length: channels[0]?.length,
        expectedLength: width * height
      });

      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Create ImageData
      const imageData = ctx.createImageData(width, height);
      const data = imageData.data;

      // Convert raster data to RGBA
      // geotiff returns data as separate channels, and may need normalization
      const numChannels = channels.length;

      // Determine if we need to normalize the data (for 16-bit TIFFs, etc.)
      // Check the max value in the first channel to determine bit depth
      let maxVal = 0;
      const firstChannel = channels[0];
      for (let i = 0; i < Math.min(1000, firstChannel.length); i++) {
        if (firstChannel[i] > maxVal) maxVal = firstChannel[i];
      }

      // If max value is greater than 255, we need to normalize
      const needsNormalization = maxVal > 255;
      const normalizeFactor = needsNormalization ? (255 / maxVal) : 1;

      console.log('TIFF data info:', {
        numChannels,
        maxSampleValue: maxVal,
        needsNormalization,
        normalizeFactor
      });

      for (let i = 0; i < width * height; i++) {
        if (numChannels >= 3) {
          // RGB or RGBA
          data[i * 4] = Math.min(255, Math.round(channels[0][i] * normalizeFactor));     // R
          data[i * 4 + 1] = Math.min(255, Math.round(channels[1][i] * normalizeFactor)); // G
          data[i * 4 + 2] = Math.min(255, Math.round(channels[2][i] * normalizeFactor)); // B
          data[i * 4 + 3] = numChannels >= 4 ? Math.min(255, Math.round(channels[3][i] * normalizeFactor)) : 255; // A
        } else if (numChannels === 1) {
          // Grayscale
          const gray = Math.min(255, Math.round(channels[0][i] * normalizeFactor));
          data[i * 4] = gray;     // R
          data[i * 4 + 1] = gray; // G
          data[i * 4 + 2] = gray; // B
          data[i * 4 + 3] = 255;  // A
        }
      }

      // Put image data on canvas
      ctx.putImageData(imageData, 0, 0);

      // Convert canvas to data URL
      const dataUrl = canvas.toDataURL('image/png');
      console.log('TIFF successfully converted to PNG data URL:', {
        dataUrlLength: dataUrl.length,
        dataUrlPrefix: dataUrl.substring(0, 50),
        canvasWidth: canvas.width,
        canvasHeight: canvas.height
      });

      return dataUrl;
    } catch (error) {
      console.error('Failed to decode TIFF image:', error);
      throw error;
    }
  }, []);

  // Set image URL when contentUrl is available
  useEffect(() => {
    if (!contentUrl) {
      setImageUrl(null);
      setLoading(true);
      return;
    }

    console.log('Loading image:', contentId, 'via', isDirectDownload ? 'presigned URL' : 'streaming');

    // Check if this is a TIFF image
    const isTiffFile = isTiffImage(metadata.mimeType, metadata.fileName);
    setIsTiff(isTiffFile);

    if (isTiffFile) {
      // Decode TIFF image asynchronously
      setLoading(true);
      decodeTiffImage(contentUrl)
        .then(decodedUrl => {
          setImageUrl(decodedUrl);
          setLoading(false);
        })
        .catch(error => {
          console.error('TIFF decoding failed:', error);
          setLoading(false);
          const viewerError: ViewerError = {
            code: 'DECODE_ERROR',
            message: 'Failed to decode TIFF image',
            retryable: false,
          };
          onError?.(viewerError);
        });
    } else {
      // For other images, use the URL directly whether it's presigned or streaming
      setImageUrl(contentUrl);
      setLoading(false);
    }

  }, [contentId, contentUrl, isDirectDownload, metadata.mimeType, metadata.fileName, isTiffImage, decodeTiffImage, onError]);

  // Handle image load
  const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.target as HTMLImageElement;
    setNaturalDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
    setLoading(false);
    onLoad?.();
  }, [onLoad]);

  const handleImageError = useCallback(() => {
    setLoading(false);
    const error: ViewerError = {
      code: 'DECODE_ERROR',
      message: 'Failed to decode image',
      retryable: false,
    };
    onError?.(error);
  }, [onError]);

  // Pan handling
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!allowPan || zoom <= 1) return;
    
    setIsPanning(true);
    setPanStart({ x: event.clientX, y: event.clientY });
    event.preventDefault();
  }, [allowPan, zoom]);

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isPanning || !panStart || !allowPan) return;

    const deltaX = event.clientX - panStart.x;
    const deltaY = event.clientY - panStart.y;
    
    setPanOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
    
    setPanStart({ x: event.clientX, y: event.clientY });
    event.preventDefault();
  }, [isPanning, panStart, allowPan]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setPanStart(null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPanning(false);
    setPanStart(null);
  }, []);

  // Zoom handling
  const handleWheel = useCallback((event: React.WheelEvent) => {
    if (!allowZoom || !onZoomChange) return;

    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.min(maxZoom, Math.max(minZoom, zoom + delta));
    
    if (newZoom !== zoom) {
      onZoomChange(newZoom);
      
      // Reset pan when zooming out to 1x or below
      if (newZoom <= 1) {
        setPanOffset({ x: 0, y: 0 });
      }
    }
  }, [allowZoom, onZoomChange, zoom, maxZoom, minZoom]);

  // Double-click to fit or zoom
  const handleDoubleClick = useCallback(() => {
    if (!allowZoom || !onZoomChange || !naturalDimensions || !containerRef.current) return;

    if (zoom === 1) {
      // Zoom to fit container
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const scaleX = containerRect.width / naturalDimensions.width;
      const scaleY = containerRect.height / naturalDimensions.height;
      const optimalZoom = Math.min(scaleX, scaleY, maxZoom);
      onZoomChange(optimalZoom);
    } else {
      // Reset to 1x
      onZoomChange(1);
      setPanOffset({ x: 0, y: 0 });
    }
  }, [allowZoom, onZoomChange, zoom, naturalDimensions, maxZoom]);

  if (loading) {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading image...
        </Typography>
      </Box>
    );
  }

  if (!imageUrl) {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Failed to load image
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className={className}
      style={style}
      sx={{
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        cursor: zoom > 1 && allowPan ? (isPanning ? 'grabbing' : 'grab') : 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onWheel={handleWheel}
      onDoubleClick={handleDoubleClick}
    >
      {/* Pan/Zoom instructions */}
      {allowZoom && (
        <Fade in={!isPanning} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              p: 1,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ZoomInIcon fontSize="small" />
            <Typography variant="caption">
              Scroll to zoom, {zoom > 1 ? 'drag to pan, ' : ''}double-click to fit
            </Typography>
          </Box>
        </Fade>
      )}

      {/* TIFF indicator */}
      {isTiff && (
        <Fade in={true} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              backgroundColor: 'rgba(76, 175, 80, 0.9)',
              color: 'white',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}
          >
            TIFF
          </Box>
        </Fade>
      )}

      <Box
        component="img"
        ref={imageRef}
        src={imageUrl}
        alt={metadata.fileName}
        onLoad={handleImageLoad}
        onError={handleImageError}
        sx={{
          maxWidth: zoom <= 1 ? '100%' : 'none',
          maxHeight: zoom <= 1 ? '100%' : 'none',
          objectFit: zoom <= 1 ? 'contain' : 'none',
          transform: `scale(${zoom}) translate(${panOffset.x}px, ${panOffset.y}px)`,
          transformOrigin: 'center center',
          transition: isPanning ? 'none' : 'transform 0.2s ease-out',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
        draggable={false}
      />

      {/* Image info overlay */}
      {naturalDimensions && (
        <Fade in={true} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
            }}
          >
            {naturalDimensions.width} × {naturalDimensions.height}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default ImageViewer;